<script setup lang="ts">
    import { Info } from 'lucide-vue-next';
    import type { ControlError } from '@/shared/forms';
    import { Label } from '@/shared/ui/atoms/Label';
    import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/molecules/Collapsible';
    import { useFieldState } from './use-field';

    const props = withDefaults(
        defineProps<{
            label?: string;
            /** Help text under the control. An error replaces it */
            description?: string;
            /** Text behind the info button next to the label */
            hint?: string;
            /** One error message, for plain `v-model` use */
            error?: string;
            /** Errors from the Control contract (`form.x().control.errors`). Only the first one shows */
            errors?: readonly ControlError[];
            invalid?: boolean;
            required?: boolean;
            disabled?: boolean;
            /** Id of the control. Generated when unset */
            id?: string;
            /** Accessible name of the hint button */
            hintLabel?: string;
        }>(),
        {
            label: undefined,
            description: undefined,
            hint: undefined,
            error: undefined,
            errors: () => [],
            invalid: false,
            required: false,
            disabled: false,
            id: undefined,
            hintLabel: 'More information',
        },
    );

    const slots = defineSlots<{
        default(props: { id: string; describedBy: string | undefined; invalid: boolean }): unknown;
        label?(): unknown;
        description?(): unknown;
        hint?(): unknown;
    }>();

    const { label, description, hint, error, errors, invalid, id } = toRefs(props);
    const { controlId, descriptionId, errorId, message, isInvalid, hasLabel, hasHint, showDescription, describedBy } = useFieldState(
        { label, description, hint, error, errors, invalid, id },
        slots,
    );
</script>

<template>
    <Collapsible
        data-slot="field"
        class="ui-field"
        :data-invalid="isInvalid || undefined"
        :data-disabled="disabled || undefined"
    >
        <div
            v-if="hasLabel || hasHint"
            class="ui-field__top"
        >
            <div class="ui-field__header">
                <Label
                    v-if="hasLabel"
                    class="ui-field__label"
                    :for="controlId"
                    :required="required"
                    :disabled="disabled"
                    :invalid="isInvalid"
                >
                    <slot name="label">{{ label }}</slot>
                </Label>
                <CollapsibleTrigger
                    v-if="hasHint"
                    class="ui-field__hint-button"
                    :aria-label="hintLabel"
                >
                    <Info />
                </CollapsibleTrigger>
            </div>

            <CollapsibleContent v-if="hasHint">
                <div class="ui-field__hint-space">
                    <div class="ui-field__hint">
                        <slot name="hint">{{ hint }}</slot>
                    </div>
                </div>
            </CollapsibleContent>
        </div>

        <slot
            :id="controlId"
            :described-by="describedBy"
            :invalid="isInvalid"
        />

        <div
            v-if="showDescription"
            :id="descriptionId"
            class="ui-field__description"
        >
            <slot name="description">{{ description }}</slot>
        </div>

        <div
            v-if="message"
            :id="errorId"
            role="alert"
            class="ui-field__error"
        >
            {{ message }}
        </div>
    </Collapsible>
</template>

<style scoped src="./field.css"></style>

<style scoped>
    @layer components {
        .ui-field {
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
        }
    }
</style>
